import React, { useState, useEffect, useCallback } from 'react';
import { invWastageService, invMovementService, invItemService } from '../../services/inventoryService';
import { card, pageWrap, emptyState, fmtQty, fmtCost, fmtDate } from './inventoryStyles';

const MOVEMENT_LABELS = {
  purchase:             'Purchase (GRN)',
  stock_adjustment:     'Stock Adjustment',
  wastage:              'Wastage',
  sale_deduction:       'Sale (Auto)',
  sale_reversal:        'Sale Reversal',
  stock_audit_variance: 'Audit Variance',
  opening_stock:        'Opening Stock',
};

const tab = (active, label, id, onSelect) => (
  <button
    key={id}
    onClick={() => onSelect(id)}
    style={{
      padding: '8px 18px', borderRadius: '8px', border: 'none', cursor: 'pointer',
      fontWeight: active === id ? '700' : '500', fontSize: '0.88rem',
      background: active === id ? '#FF4444' : 'transparent',
      color: active === id ? 'white' : '#555',
      transition: 'all 0.15s',
    }}
  >
    {label}
  </button>
);

const InventoryReports = ({ restaurant }) => {
  const [activeTab, setActiveTab] = useState('movements');
  const [movements, setMovements] = useState([]);
  const [wastage, setWastage]     = useState([]);
  const [items, setItems]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filterItem, setFilterItem] = useState('');
  const restaurantId = restaurant?.id;

  // Date range: last 30 days
  const [startDate] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 30); return d; });

  const load = useCallback(async () => {
    setLoading(true);
    const [mRes, wRes, iRes] = await Promise.all([
      invMovementService.getAll(restaurantId, { startDate }),
      invWastageService.getAll(restaurantId),
      invItemService.getAll(restaurantId),
    ]);
    setMovements(mRes.data || []);
    setWastage(wRes.data || []);
    setItems((iRes.data || []).filter(i => i.is_active));
    setLoading(false);
  }, [restaurantId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // ── Derived: consumption by item (sales + wastage last 30 days) ──
  const consumption = items.map(item => {
    const sales = movements.filter(m => m.inventory_item_id === item.id && m.movement_type === 'sale_deduction')
      .reduce((s, m) => s + Math.abs(m.quantity_change), 0);
    const waste = wastage.filter(w => w.inventory_item_id === item.id)
      .reduce((s, w) => s + (w.quantity || 0), 0);
    const total = sales + waste;
    return { ...item, sales, waste, total, value: total * (item.avg_unit_cost || 0) };
  }).filter(i => i.total > 0).sort((a, b) => b.value - a.value);

  // ── Derived: wastage by reason ──
  const wastageByReason = {};
  wastage.forEach(w => {
    const r = w.reason || 'other';
    if (!wastageByReason[r]) wastageByReason[r] = { qty: 0, value: 0 };
    wastageByReason[r].qty += w.quantity || 0;
    wastageByReason[r].value += (w.quantity || 0) * (w.inventory_item?.avg_unit_cost || 0);
  });

  const filteredMovements = filterItem
    ? movements.filter(m => m.inventory_item_id === filterItem)
    : movements;

  const totalWastageValue = wastage.reduce((s, w) => s + (w.quantity || 0) * (w.inventory_item?.avg_unit_cost || 0), 0);

  return (
    <div style={pageWrap}>
      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '800', color: '#111' }}>Inventory Reports</h2>
          <p style={{ margin: '4px 0 0', color: '#888', fontSize: '0.88rem' }}>Last 30 days of stock activity</p>
        </div>

        {/* Tab bar */}
        <div style={{ display: 'flex', gap: '4px', background: '#f3f4f6', padding: '4px', borderRadius: '10px', marginBottom: '24px', width: 'fit-content' }}>
          {tab(activeTab, 'Stock Movements', 'movements', setActiveTab)}
          {tab(activeTab, 'Consumption', 'consumption', setActiveTab)}
          {tab(activeTab, 'Wastage Analysis', 'wastage', setActiveTab)}
        </div>

        {loading ? (
          <div style={{ ...card, ...emptyState }}>Loading…</div>
        ) : (
          <>
            {/* ── Stock Movements ── */}
            {activeTab === 'movements' && (
              <div style={{ ...card, overflow: 'hidden' }}>
                <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <select
                    value={filterItem}
                    onChange={e => setFilterItem(e.target.value)}
                    style={{ padding: '7px 12px', borderRadius: '8px', border: '1.5px solid #e5e7eb', fontSize: '0.88rem', color: '#333', background: 'white' }}
                  >
                    <option value="">All items</option>
                    {items.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                  </select>
                  <span style={{ color: '#888', fontSize: '0.82rem' }}>{filteredMovements.length} entries</span>
                </div>
                {filteredMovements.length === 0 ? (
                  <div style={emptyState}>No stock movements in this period.</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                    <thead>
                      <tr style={{ background: '#f9fafb', borderBottom: '2px solid #f3f4f6' }}>
                        {['Date', 'Item', 'Type', 'Change', 'Before', 'After', 'Notes'].map(h => (
                          <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontWeight: '700', color: '#555', fontSize: '0.8rem' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredMovements.map(m => {
                        const isPos = m.quantity_change > 0;
                        return (
                          <tr key={m.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '12px 14px', color: '#888', fontSize: '0.82rem' }}>{fmtDate(m.created_at)}</td>
                            <td style={{ padding: '12px 14px', fontWeight: '600', color: '#333' }}>{m.inventory_item?.name}</td>
                            <td style={{ padding: '12px 14px', color: '#555' }}>{MOVEMENT_LABELS[m.movement_type] || m.movement_type}</td>
                            <td style={{ padding: '12px 14px', fontWeight: '700', color: isPos ? '#16a34a' : '#dc2626' }}>
                              {isPos ? '+' : ''}{fmtQty(m.quantity_change)}
                            </td>
                            <td style={{ padding: '12px 14px', color: '#888' }}>{fmtQty(m.stock_before)}</td>
                            <td style={{ padding: '12px 14px', color: '#333', fontWeight: '600' }}>{fmtQty(m.stock_after)}</td>
                            <td style={{ padding: '12px 14px', color: '#aaa', fontSize: '0.82rem' }}>{m.remarks || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* ── Consumption ── */}
            {activeTab === 'consumption' && (
              <div style={{ ...card, overflow: 'hidden' }}>
                {consumption.length === 0 ? (
                  <div style={emptyState}>No consumption data in this period.</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                    <thead>
                      <tr style={{ background: '#f9fafb', borderBottom: '2px solid #f3f4f6' }}>
                        {['Item', 'Sales Deduction', 'Wastage', 'Total Used', 'Est. Cost'].map(h => (
                          <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontWeight: '700', color: '#555', fontSize: '0.8rem' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {consumption.map(i => (
                        <tr key={i.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '12px 14px', fontWeight: '600', color: '#333' }}>{i.name}</td>
                          <td style={{ padding: '12px 14px', color: '#555' }}>{fmtQty(i.sales)}</td>
                          <td style={{ padding: '12px 14px', color: '#dc2626' }}>{fmtQty(i.waste)}</td>
                          <td style={{ padding: '12px 14px', fontWeight: '700', color: '#333' }}>{fmtQty(i.total)}</td>
                          <td style={{ padding: '12px 14px', color: '#16a34a', fontWeight: '600' }}>{fmtCost(i.value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* ── Wastage Analysis ── */}
            {activeTab === 'wastage' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Summary card */}
                <div style={{ ...card, padding: '20px 24px', background: totalWastageValue > 0 ? '#fef2f2' : undefined, border: totalWastageValue > 0 ? '1px solid #fca5a5' : undefined }}>
                  <div style={{ fontSize: '0.8rem', fontWeight: '700', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>Total Wastage Value (All Time)</div>
                  <div style={{ fontSize: '2rem', fontWeight: '800', color: '#dc2626' }}>{fmtCost(totalWastageValue)}</div>
                </div>

                {/* By reason */}
                <div style={card}>
                  <div style={{ padding: '16px 20px', fontWeight: '700', color: '#333', borderBottom: '1px solid #f3f4f6', fontSize: '0.95rem' }}>By Reason</div>
                  {Object.entries(wastageByReason).length === 0 ? (
                    <div style={emptyState}>No wastage recorded.</div>
                  ) : (
                    <div style={{ padding: '16px 20px' }}>
                      {Object.entries(wastageByReason).sort((a, b) => b[1].value - a[1].value).map(([reason, data]) => (
                        <div key={reason} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f9fafb' }}>
                          <div style={{ fontWeight: '600', color: '#333', textTransform: 'capitalize' }}>{reason.replace('_', ' ')}</div>
                          <div style={{ display: 'flex', gap: '24px', fontSize: '0.88rem' }}>
                            <span style={{ color: '#888' }}>{fmtQty(data.qty)} units</span>
                            <span style={{ color: '#dc2626', fontWeight: '700' }}>{fmtCost(data.value)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Detailed table */}
                <div style={{ ...card, overflow: 'hidden' }}>
                  <div style={{ padding: '14px 20px', fontWeight: '700', color: '#333', borderBottom: '1px solid #f3f4f6', fontSize: '0.95rem' }}>All Wastage Entries</div>
                  {wastage.length === 0 ? (
                    <div style={emptyState}>No wastage recorded.</div>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                      <thead>
                        <tr style={{ background: '#f9fafb', borderBottom: '2px solid #f3f4f6' }}>
                          {['Date', 'Item', 'Reason', 'Qty', 'Value', 'Notes'].map(h => (
                            <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontWeight: '700', color: '#555', fontSize: '0.8rem' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {wastage.map(w => (
                          <tr key={w.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '12px 14px', color: '#888', fontSize: '0.82rem' }}>{fmtDate(w.wastage_date || w.created_at)}</td>
                            <td style={{ padding: '12px 14px', fontWeight: '600', color: '#333' }}>{w.inventory_item?.name}</td>
                            <td style={{ padding: '12px 14px', color: '#555', textTransform: 'capitalize' }}>{(w.reason || '').replace('_', ' ')}</td>
                            <td style={{ padding: '12px 14px', color: '#dc2626', fontWeight: '700' }}>{fmtQty(w.quantity)}</td>
                            <td style={{ padding: '12px 14px', color: '#dc2626', fontWeight: '600' }}>{fmtCost((w.quantity || 0) * (w.inventory_item?.avg_unit_cost || 0))}</td>
                            <td style={{ padding: '12px 14px', color: '#888', fontSize: '0.82rem' }}>{w.notes || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default InventoryReports;
